type TokenData = {
  accessToken: string;
  expiresTime: number;
};

export default defineNuxtPlugin(async (nuxtApp) => {
  const rootStore = useRootStore();
  const { EcomService } = await import('~/services/ecom.service');

  // Local state for refresh control with mutex
  let isRefreshInProgress = false;
  let refreshPromise: Promise<any> | null = null;

  // Utility: get token data from cookie (server) or store (client)
  function getAccessTokenData() {
    if (import.meta.client && rootStore.access_token_data) {
      return rootStore.access_token_data;
    }
    const cookie = useCookie<{
      accessToken: string;
      expiresTime: number;
    }>('ecomApiAccessToken');
    return cookie.value;
  }

  // Utility: set token data cookie and update store + injection
  function updateAccessToken(tokenData: { accessToken: string; expiresTime: number }) {
    // Only set the cookie if it's not already set as httpOnly by the server
    // or if we are on the server.
    if (import.meta.server) {
      const cookie = useCookie<{ accessToken: string; expiresTime: number }>('ecomApiAccessToken', {
        expires: new Date(tokenData.expiresTime * 1000),
        secure: true,
        sameSite: 'lax',
        httpOnly: true, // Key requirement for Beagle Security
      });
      cookie.value = tokenData as any;
    }

    // Always update the store
    rootStore.access_token = tokenData.accessToken;
    rootStore.access_token_data = tokenData;
  }

  // Token validity check convenience
  function isValidToken(tokenData?: TokenData | null): tokenData is TokenData {
    if (!tokenData) return false;
    return EcomService.isTokenValid(
      tokenData.accessToken,
      tokenData.expiresTime,
      EcomService.accessTokenExpiryBuffer
    );
  }

  // Calculate refresh interval in milliseconds
  function calculateRefreshInterval(): number {
    const tokenData = getAccessTokenData();
    if (!tokenData?.expiresTime) {
      return EcomService.accessTokenPollInterval * 1000;
    }

    const nowSec = Math.floor(Date.now() / 1000);
    const timeLeft = tokenData.expiresTime - nowSec;
    if (timeLeft <= 0) {
      // Token expired, refresh immediately (short delay)
      return 5000; // 5 seconds to retry quickly
    }

    // Ideally refresh at 75% of remaining time or configured buffer, whichever is smaller
    // But never refresh more frequently than every 1 minute
    const candidate = Math.min(timeLeft * 0.75, EcomService.accessTokenExpiryBuffer);
    const minIntervalSec = 60; // 1 minute minimal wait
    const intervalSec = Math.max(candidate, minIntervalSec);

    return intervalSec * 1000; // ms
  }

  // Schedule refresh token with adaptive timing
  function scheduleNextRefresh(delayMs: number) {
    if (rootStore.accessTokenTimeoutId) {
      clearTimeout(rootStore.accessTokenTimeoutId);
    }

    rootStore.accessTokenTimeoutId = setTimeout(async () => {
      if (isRefreshInProgress) {
        // Another refresh happening; reschedule shortly
        scheduleNextRefresh(30000); // 30 seconds
        return;
      }

      try {
        const tokenData = getAccessTokenData();
        if (!isValidToken(tokenData)) {
          // Use mutex pattern - if refresh is in progress, wait for it
          if (isRefreshInProgress && refreshPromise) {
            await refreshPromise;
          } else {
            isRefreshInProgress = true;
            refreshPromise = EcomService.fetchToken()
              .then((newTokenData: any) => {
                if (newTokenData?.accessToken && newTokenData.expiresTime) {
                  updateAccessToken(newTokenData);
                  console.info('[EcomService] Access token refreshed');
                } else {
                  console.warn('[EcomService] Fetched token data is incomplete:', newTokenData);
                }
                isRefreshInProgress = false;
                refreshPromise = null;
                return newTokenData;
              })
              .catch((err) => {
                isRefreshInProgress = false;
                refreshPromise = null;
                throw err;
              });
            await refreshPromise;
          }
        } else {
          // Token is still valid, update rootStore & injection just in case
          if (tokenData?.accessToken) {
            (rootStore.access_token as any) = tokenData.accessToken;
          }
        }

        // Schedule next refresh based on new timing
        scheduleNextRefresh(calculateRefreshInterval());
      } catch (error) {
        console.error('[EcomService] Failed to refresh access token:', error);
        isRefreshInProgress = false;
        refreshPromise = null;

        // Retry in 5 minutes on failure
        scheduleNextRefresh(300000);
      }
    }, delayMs);
  }

  // Sync reactive store and injection when cookie changes client-side
  // DEPRECATED: With httpOnly: true, client cannot watch the cookie directly.
  // We rely on Pinia hydration and manual updates from EcomService.fetchToken().
  /*
  if (import.meta.client) {
    ...
  }
  */

  // Server-side initial token check & fetch if needed
  if (import.meta.server) {
    const tokenData = getAccessTokenData();
    if (!isValidToken(tokenData)) {
      try {
        const fetched = (await EcomService.fetchToken()) as any;
        if (fetched?.accessToken && fetched.expiresTime) {
          updateAccessToken(fetched);
          nuxtApp.provide('ecomAccessToken', fetched.accessToken);
        } else {
          console.warn('[EcomService] Initial fetched token incomplete:', fetched);
        }
      } catch (error) {
        console.error('[EcomService] Initial token fetch failed on server:', error);
      }
    } else {
      // Valid token found, sync states
      if (tokenData?.accessToken) {
        rootStore.access_token = tokenData.accessToken;
        rootStore.access_token_data = tokenData;
        nuxtApp.provide('ecomAccessToken', tokenData.accessToken);
      }
    }
  }

  // Client-side: start refresh cycle
  if (import.meta.client) {
    const initialDelay = calculateRefreshInterval();
    scheduleNextRefresh(initialDelay);
  }
});
